import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    InputAdornment,
    MenuItem,
    Chip,
    Typography,
    TablePagination,
    Button,
} from "@mui/material";
import { Search, ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import scanLogService from "../../services/scanLogService";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";

// Read-only audit trail of every item committed via the Scan In / Scan Out
// page (AdminScan.jsx) — who scanned it, which direction, and when. Each
// row is one ScanLog entry, written server-side in
// shipmentItemController.bulkUpdateTrackingStatus when the request carries
// a scanMode.
const AdminScanLogs = () => {
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [direction, setDirection] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const loadLogs = async (overrides = {}) => {
        setLoading(true);
        try {
            const { data } = await scanLogService.getAll({
                page: page + 1,
                limit: rowsPerPage,
                search: search || undefined,
                direction: direction || undefined,
                ...overrides,
            });
            setLogs(data.data || []);
            setTotal(data.pagination?.total || 0);
        } catch {
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, direction]);

    const handleSearch = (e) => {
        if (e.key === "Enter") {
            setPage(0);
            loadLogs({ page: 1, search: e.target.value });
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Button
                startIcon={<ArrowBack />}
                size="small"
                onClick={() => navigate("/admin/scan")}
                sx={{ alignSelf: "flex-start" }}
            >
                Back
            </Button>

            <PageHeader
                title="Scan Log"
                subtitle={`${total} scan${total !== 1 ? "s" : ""} recorded — who scanned what, in or out`}
            />

            <Card>
                <Box
                    sx={{
                        p: 2,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "wrap",
                    }}
                >
                    <TextField
                        size="small"
                        placeholder="Search tracking number…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleSearch}
                        sx={{ width: { xs: "100%", sm: 260 } }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ fontSize: 16, color: "text.disabled" }} />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Direction"
                        value={direction}
                        onChange={(e) => {
                            setDirection(e.target.value);
                            setPage(0);
                        }}
                        sx={{ width: { xs: "100%", sm: 160 } }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="in">Scan In</MenuItem>
                        <MenuItem value="out">Scan Out</MenuItem>
                    </TextField>
                </Box>

                {loading ? (
                    <SkeletonLoader rows={Math.min(8, rowsPerPage)} />
                ) : logs.length === 0 ? (
                    <EmptyState
                        icon="📷"
                        title="No scans found"
                        description="Items scanned in or out at the warehouse will appear here."
                    />
                ) : (
                    <>
                        <Box sx={{ overflowX: "auto" }}>
                            <Table sx={{ minWidth: 850 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Tracking #</TableCell>
                                        <TableCell>Shipment</TableCell>
                                        <TableCell>Customer</TableCell>
                                        <TableCell>Direction</TableCell>
                                        <TableCell>New Status</TableCell>
                                        <TableCell>Scanned By</TableCell>
                                        <TableCell>When</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.map((log) => (
                                        <TableRow key={log._id} hover>
                                            <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                {log.trackingNumber}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                {log.shipment?.trackingNumber || "—"}
                                            </TableCell>
                                            <TableCell>
                                                {log.shipment?.customer ? (
                                                    <>
                                                        <Typography variant="body2">
                                                            {`${log.shipment.customer.firstName || ""} ${log.shipment.customer.lastName || ""}`.trim()}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {log.shipment.customer.email}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    "—"
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={log.direction === "in" ? "Scan In" : "Scan Out"}
                                                    size="small"
                                                    color={log.direction === "in" ? "success" : "info"}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                                                {log.itemStatus}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {log.scannedBy
                                                        ? `${log.scannedBy.firstName || ""} ${log.scannedBy.lastName || ""}`.trim()
                                                        : "—"}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {log.scannedBy?.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
                                                {dayjs(log.createdAt).format("MMM D, YYYY h:mm A")}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(+e.target.value);
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 20, 50]}
                            sx={{ borderTop: "1px solid", borderColor: "divider" }}
                        />
                    </>
                )}
            </Card>
        </Box>
    );
};

export default AdminScanLogs;
